<template>
  <Field
    class="email-field"
    placeholder="Please enter your email"
    @input="onInput"
    @blur="onBlur"
    v-bind="$attrs"
    v-model="email"
    :error="!!error"
  >
    <template #suffix>
      <Button
        class="email-field__button"
        :block="false"
        @click="onSend"
        :disabled="disabled"
        :loading="loading"
        type="primary"
      >
        <template v-if="isActive">
          {{ countdown }}
        </template>
        <template v-else-if="isSent">
          Send again
        </template>
        <template v-else>
          Get Code
        </template>
      </Button>
    </template>
  </Field>
  <p
    class="email-field-error"
    v-if="error"
  >
    <i class="email-field-error__icon" />
    {{ error }}
  </p>
  <p
    class="email-field-hint"
    v-else-if="hint"
  >
    <i class="email-field-hint__icon" />
    We sent verification code to your email!
  </p>
</template>

<script setup>
import Field from '../Field/index.vue'
import { Button } from '@/components/Button'
import { sendVerifyCode } from '@/api/user'
import { useCountdown } from '@vueuse/core'
import { buriedPointUtil, validateEmail } from '@/utils/utils'
import { isMobile } from '@/utils/constants'

const email = defineModel({ type: String })

defineOptions({
  inheritAttrs: false
})

const loading = ref(false)
const { remaining: countdown, start, isActive } = useCountdown()
const isSent = ref(false)
const error = ref('')
const hint = ref(false)
const disabled = computed(() => {
  if (email.value === '' || email.value == null) return true
  try {
    checkEmail()
  } catch {
    return true
  }
  return isActive.value
})

const checkEmail = () => {
  if (email.value === '' || email.value == null) {
    throw new Error('The email address is required.')
  } else if (email.value.length >= 100) {
    throw new Error('Cannot exceed 100 characters.')
  } else if (!validateEmail(email.value)) {
    throw new Error('Please enter a valid email address.')
  }
}

const onInput = () => {
  error.value = null
  hint.value = false
}

const onBlur = () => {
  try {
    checkEmail()
    error.value = null
  } catch (e) {
    error.value = e.message
  }
}

const onSend = async () => {
  try {
    loading.value = true
    buriedPointUtil({ eventId: isMobile ? 'MPLG201' : 'PLG201' })
    const { code, msg: errorMsg } = await sendVerifyCode({ loginAccount: email.value })

    if (code === 200) {
      start(60)
      hint.value = true
      isSent.value = true
    } else {
      throw new Error(errorMsg)
    }
  } catch (e) {
    console.error(e)
    error.value = e.message ?? 'Network Error'
  } finally {
    loading.value = false
  }
}
</script>

<style lang="less" scoped>
.email-field {
  overflow: hidden;

  &__button {
    flex-shrink: 0;
    width: 88px;
    border-radius: 0;
    font-size: 12px;
  }

  &-hint, &-error {
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 4px;
    font-family: mon-500;
    font-size: 10px;
  }

  &-hint {
    color: #1153D6;
  }

  &-error {
    color: #F53131;
  }
}
</style>
